import type React from 'react';

export interface DonutSegment {
  key: string;
  /** Share of the ring, 0..100. Segments should sum to ~100. */
  pct: number;
  color: string;
}

// A point on a circle of radius R, at angle t measured clockwise from 12 o'clock
// (screen coords, y down): (cx + R·sint, cy − R·cost).
const polar = (cx: number, cy: number, R: number, t: number) => ({
  x: cx + R * Math.sin(t),
  y: cy - R * Math.cos(t),
});

/**
 * A filled "thick arc" segment between angles t0..t1 (radians, clockwise from
 * 12 o'clock) with rounded corners of radius `cr`. Built as a closed annular
 * sector — outer arc, end cap, inner arc, start cap — rather than a round-capped
 * stroke, so the corner radius is tunable instead of locked at stroke/2.
 */
function sectorPath(
  cx: number,
  cy: number,
  rMid: number,
  stroke: number,
  t0: number,
  t1: number,
  cr: number,
): string {
  const h = stroke / 2;
  const ro = rMid + h;
  const ri = rMid - h;
  // Keep corners within the band thickness and the segment's own arc length.
  const radius = Math.max(0, Math.min(cr, h, ((t1 - t0) * ri) / 2.2));
  const go = radius / ro; // angular inset of a corner on the outer edge
  const gi = radius / ri; // …and on the inner edge
  const A = polar(cx, cy, ro, t0 + go);
  const B = polar(cx, cy, ro, t1 - go);
  const C = polar(cx, cy, ro - radius, t1);
  const D = polar(cx, cy, ri + radius, t1);
  const E = polar(cx, cy, ri, t1 - gi);
  const F = polar(cx, cy, ri, t0 + gi);
  const G = polar(cx, cy, ri + radius, t0);
  const H = polar(cx, cy, ro - radius, t0);
  const outerLarge = t1 - go - (t0 + go) > Math.PI ? 1 : 0;
  const innerLarge = t1 - gi - (t0 + gi) > Math.PI ? 1 : 0;
  const n = (v: number) => v.toFixed(2);
  return [
    `M ${n(A.x)} ${n(A.y)}`,
    `A ${n(ro)} ${n(ro)} 0 ${outerLarge} 1 ${n(B.x)} ${n(B.y)}`,
    `A ${n(radius)} ${n(radius)} 0 0 1 ${n(C.x)} ${n(C.y)}`,
    `L ${n(D.x)} ${n(D.y)}`,
    `A ${n(radius)} ${n(radius)} 0 0 1 ${n(E.x)} ${n(E.y)}`,
    `A ${n(ri)} ${n(ri)} 0 ${innerLarge} 0 ${n(F.x)} ${n(F.y)}`,
    `A ${n(radius)} ${n(radius)} 0 0 1 ${n(G.x)} ${n(G.y)}`,
    `L ${n(H.x)} ${n(H.y)}`,
    `A ${n(radius)} ${n(radius)} 0 0 1 ${n(A.x)} ${n(A.y)}`,
    'Z',
  ].join(' ');
}

/**
 * A hollow ring split into proportional segments, drawn clockwise from 12
 * o'clock as filled pills with softly rounded corners and a clear gap between
 * each (like the GaugeArc below). No background track — the gaps read as clean
 * breaks, so callers should pass shares that sum to ~100. Purely presentational.
 */
const Donut: React.FC<{
  segments: DonutSegment[];
  size?: number;
  stroke?: number;
  /** Visible gap between adjacent segments, in px (constant regardless of size). */
  gapPx?: number;
  /** Corner radius of each segment, in px (≤ stroke/2 = fully round). */
  capRadius?: number;
}> = ({ segments, size = 104, stroke = 17, gapPx = 5, capRadius = 4.5 }) => {
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  // Each segment's cap face sits on its boundary, so a gap of `gapPx` just means
  // insetting each side of every boundary by half of it (as a ring fraction).
  const inset = gapPx / 2 / circ;
  let cum = 0;
  return (
    <svg
      className="donut"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      {segments.map((s) => {
        const f0 = cum;
        const f1 = cum + s.pct / 100;
        cum = f1;
        const a = f0 + inset;
        const b = f1 - inset;
        // Too slim to survive the gap inset — show a centered dot rather than
        // dropping the share entirely.
        if (b <= a) {
          const c = polar(cx, cy, r, (f0 + f1) * Math.PI);
          return (
            <circle
              key={s.key}
              cx={c.x}
              cy={c.y}
              r={stroke / 2}
              fill={s.color}
            />
          );
        }
        return (
          <path
            key={s.key}
            d={sectorPath(
              cx,
              cy,
              r,
              stroke,
              a * 2 * Math.PI,
              b * 2 * Math.PI,
              capRadius,
            )}
            fill={s.color}
          />
        );
      })}
    </svg>
  );
};

export default Donut;

/**
 * A gauge arc of `arcDeg`° centred on 12 o'clock — a flat-bottom semicircle at
 * the default 180°, curling its ends below the horizontal for wider sweeps.
 * Split into proportional segments left → right, each a softly rounded pill set
 * off from its neighbour by a clear gap, flush at the outer tips. No background
 * track — gaps read as clean breaks, so callers should pass shares that sum to
 * ~100. Purely presentational.
 */
export const GaugeArc: React.FC<{
  segments: DonutSegment[];
  width?: number;
  stroke?: number;
  /** Total sweep, in degrees, centred on the top and open at the bottom. */
  arcDeg?: number;
  /** Visible gap between adjacent segments, in px (constant regardless of size). */
  gapPx?: number;
  /** Corner radius of each segment, in px (≤ stroke/2 = fully round). */
  capRadius?: number;
}> = ({
  segments,
  width = 176,
  stroke = 18,
  arcDeg = 180,
  gapPx = 6,
  capRadius = 5,
}) => {
  const r = (width - stroke) / 2;
  const cx = width / 2;
  const cy = r + stroke / 2; // the top of the arc sits at stroke/2
  const halfRad = ((arcDeg / 2) * Math.PI) / 180;
  // How far the ends dip below the centre line (0 at ≤180°, grows beyond it).
  const dip = Math.max(0, -Math.cos(halfRad)) * r;
  const height = r + dip + stroke;
  const arcLen = 2 * Math.PI * r * (arcDeg / 360);
  const inset = gapPx / 2 / arcLen; // gap as a fraction of the arc
  // Angle (radians, clockwise from top) at fraction f along the arc.
  const ang = (f: number) => ((-arcDeg / 2 + f * arcDeg) * Math.PI) / 180;
  let cum = 0;
  return (
    <svg
      className="donut donut-gauge"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      {segments.map((s, i) => {
        const f0 = cum;
        const f1 = cum + s.pct / 100;
        cum = f1;
        // Flush at the outer tips; gapped only between adjacent segments.
        const a = i === 0 ? f0 : f0 + inset;
        const b = i === segments.length - 1 ? f1 : f1 - inset;
        if (b <= a) {
          const c = polar(cx, cy, r, ang((f0 + f1) / 2));
          return (
            <circle
              key={s.key}
              cx={c.x}
              cy={c.y}
              r={stroke / 2}
              fill={s.color}
            />
          );
        }
        return (
          <path
            key={s.key}
            d={sectorPath(cx, cy, r, stroke, ang(a), ang(b), capRadius)}
            fill={s.color}
          />
        );
      })}
    </svg>
  );
};
